import {createElement, forwardRef, type ComponentType} from 'react';
import {defaults} from 'lodash';
import {unchanging} from '@packages/utilities/component';
import {styled} from '@packages/utilities/styled';
import {adjustMargin} from '@packages/utilities/inline-style-utils';
import type {CSSProperties} from '@packages/utilities/css-properties-type';

type FlexDirectProps = {
  grow?: number;
  shrink?: number;
  basis?: string;
};

type FlexGeneralProps = {
  align?: keyof typeof alignments;
  justify?: keyof typeof justifications;
  wrap?: keyof typeof wrapping;
};

type GeneralProps = {
  inline?: boolean;
  style?: CSSProperties;
};

const alignments = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  baseline: 'baseline',
  stretch: 'stretch',
} as const;

const justifications = {
  start: 'flex-start',
  around: 'space-around',
  center: 'center',
  between: 'space-between',
  end: 'flex-end',
} as const;

const wrapping = {
  noWrap: 'nowrap',
  wrap: 'wrap',
  reverse: 'wrap-reverse',
} as const;

/**
 * PassThrough is used for the Row, Cell, and Column components to support an
 * interface like <Row grow={2} component={Note} type="warning">{children}</Row>
 */
const PassThrough: any = forwardRef(
  ({component = 'div', children, ...rest}: any, ref: any) =>
    createElement(component, {...rest, ref}, children)
);

// Each component passes its own-props type to `styled(PassThrough)<OwnProps>()`.
// That opts into styled's polymorphic output: OwnProps is validated strictly
// (typos/wrong types error) and a `component` prop plus that component's props
// are accepted — so call sites don't suppress `component`/`onClick`/`children`.
type RowColumnOwnProps = FlexGeneralProps & GeneralProps & {compact?: boolean};
type FlexOwnProps = FlexGeneralProps & FlexDirectProps & GeneralProps;
type CellOwnProps = {
  ['aria-label']?: string;
  grow?: number;
  shrink?: number;
  align?: keyof typeof alignments;
  compact?: boolean;
  style?: CSSProperties;
};

export const Row = styled(PassThrough)<RowColumnOwnProps>(
  ({inline, align, justify, compact, style, theme, wrap}) =>
    defaults(
      {
        display: inline ? 'inline-flex' : 'flex',
        flexDirection: 'row',
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly noWrap: "nowrap"; readonly wrap: "wrap"; readonly reverse: "wrap-reverse"; }'.
        flexWrap: wrapping[wrap],
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly start: "flex-start"; readonly center: "center"; readonly end: "flex-end"; readonly baseline: "baseline"; readonly stretch: "stretch"; }'.
        alignItems: alignments[align],
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly start: "flex-start"; readonly around: "space-around"; readonly center: "center"; readonly between: "space-between"; readonly end: "flex-end"; }'.
        justifyContent: justifications[justify],
      },
      adjustMargin(compact ? 0 : -theme.gutters.narrow / 2)(style)
    )
);
Row.displayName = 'LayoutRow';

export const FlexRow = styled(PassThrough)<FlexOwnProps>(
  ({
    inline,
    align,
    justify,
    style,
    wrap,
    grow = 0,
    shrink = 1,
    basis = 'auto',
  }) => ({
    display: inline ? 'inline-flex' : 'flex',
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly noWrap: "nowrap"; readonly wrap: "wrap"; readonly reverse: "wrap-reverse"; }'.
    flexWrap: wrapping[wrap],
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly start: "flex-start"; readonly center: "center"; readonly end: "flex-end"; readonly baseline: "baseline"; readonly stretch: "stretch"; }'.
    alignItems: alignments[align],
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly start: "flex-start"; readonly around: "space-around"; readonly center: "center"; readonly between: "space-between"; readonly end: "flex-end"; }'.
    justifyContent: justifications[justify],
    flex: `${grow} ${shrink} ${basis}`,
    ...style,
  })
);
FlexRow.displayName = 'FlexRow';

export const Column = styled(PassThrough)<RowColumnOwnProps>(
  ({inline, align, justify, compact, style, theme, wrap}) =>
    defaults(
      {
        display: inline ? 'inline-flex' : 'flex',
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly noWrap: "nowrap"; readonly wrap: "wrap"; readonly reverse: "wrap-reverse"; }'.
        flexWrap: wrapping[wrap],
        flexDirection: 'column',
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly start: "flex-start"; readonly center: "center"; readonly end: "flex-end"; readonly baseline: "baseline"; readonly stretch: "stretch"; }'.
        alignItems: alignments[align],
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly start: "flex-start"; readonly around: "space-around"; readonly center: "center"; readonly between: "space-between"; readonly end: "flex-end"; }'.
        justifyContent: justifications[justify],
      },
      adjustMargin(compact ? 0 : -theme.gutters.narrow / 2)(style)
    )
);
Column.displayName = 'LayoutColumn';

export const FlexColumn = styled(PassThrough)<FlexOwnProps>(
  ({
    inline,
    align,
    justify,
    style,
    wrap,
    grow = 0,
    shrink = 1,
    basis = 'auto',
  }) => ({
    display: inline ? 'inline-flex' : 'flex',
    flexDirection: 'column',
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly noWrap: "nowrap"; readonly wrap: "wrap"; readonly reverse: "wrap-reverse"; }'.
    flexWrap: wrapping[wrap],
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly start: "flex-start"; readonly center: "center"; readonly end: "flex-end"; readonly baseline: "baseline"; readonly stretch: "stretch"; }'.
    alignItems: alignments[align],
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly start: "flex-start"; readonly around: "space-around"; readonly center: "center"; readonly between: "space-between"; readonly end: "flex-end"; }'.
    justifyContent: justifications[justify],
    flex: `${grow} ${shrink} ${basis}`,
    ...style,
  })
);
FlexColumn.displayName = 'FlexColumn';

export const Cell = styled(PassThrough)<CellOwnProps>(
  ({grow, shrink, align, compact, style, theme, 'aria-label': ariaLabel}) =>
    defaults(
      {
        // Allows shrinking if necessary.
        minWidth: grow ? theme.control.height : undefined,
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly start: "flex-start"; readonly center: "center"; readonly end: "flex-end"; readonly baseline: "baseline"; readonly stretch: "stretch"; }'.
        alignSelf: alignments[align],
        flexGrow: grow,
        flexShrink: shrink,
        flexBasis: grow && 0,
        ariaLabel,
      },
      adjustMargin(compact ? 0 : theme.gutters.narrow / 2)(style)
    )
);

Cell.displayName = 'LayoutCell';

export const Divider: ComponentType<{
  compact?: boolean;
}> = unchanging(
  styled(PassThrough)(({theme, compact}) => ({
    flexShrink: 0,
    flexBasis: 1,
    alignSelf: 'stretch',
    margin: compact ? 0 : `${theme.gutters.base}px 0`,
    background: theme.colors.borderDeep,
    pointerEvents: 'none',
  }))
);

Divider.displayName = 'LayoutDivider';

export const Separator: ComponentType<{
  grow?: number;
}> = styled(PassThrough)(({grow = 1}) => ({
  flexGrow: grow,
}));

Separator.displayName = 'Separator';

export const Spacer: ComponentType<{
  wide?: boolean;
  normal?: boolean;
  base?: boolean;
  narrow?: boolean;
}> = styled(PassThrough)(
  ({theme: {gutters}}) => ({
    flexBasis: gutters.narrow,
    flexShrink: 0,
  }),
  {
    wide: ({theme: {gutters}, multiplier = 1}) => ({
      flexBasis: gutters.wide * multiplier,
    }),
    normal: ({theme: {gutters}, multiplier = 1}) => ({
      flexBasis: gutters.normal * multiplier,
    }),
    base: ({theme: {gutters}, multiplier = 1}) => ({
      flexBasis: gutters.base * multiplier,
    }),
    narrow: ({theme: {gutters}, multiplier = 1}) => ({
      flexBasis: gutters.narrow * multiplier,
    }),
  }
);

Spacer.displayName = 'Spacer';
